import { useState } from 'react';
import { Link as RouterLink, Navigate, useNavigate } from 'react-router-dom';
import { Alert, Button, Stack, TextField } from '@mui/material';
import AuthShell from '../components/AuthShell';
import { useAuth } from '../contexts/AuthContext';
import { apiErrorMessage } from '../api/client';

export default function LoginPage() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(email, password);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not log in.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Sign in"
      subtitle="Use your email and password."
      footer={
        <>
          New here?{' '}
          <RouterLink to="/#waitlist" aria-label="Join the waitlist">
            Join the waitlist
          </RouterLink>{' '}
          — we onboard founding coaches in batches.
        </>
      }
    >
      <form onSubmit={onSubmit} aria-label="Login form">
        <Stack spacing={2}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            inputProps={{ 'aria-label': 'Email address' }}
          />
          <TextField
            label="Password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            inputProps={{ 'aria-label': 'Password' }}
          />
          <Stack direction="row" justifyContent="flex-end">
            <RouterLink to="/forgot-password" aria-label="Forgot password">Forgot password?</RouterLink>
          </Stack>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={busy}
            aria-label="Sign in"
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </Stack>
      </form>
    </AuthShell>
  );
}
